import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

import AppSidebar from './AppSidebar';
import './Dashboard.css';

// --- Helper Functions (Same as Main Dashboard) ---
function getTodayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function leaderboardInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

const LEADERBOARD_PALETTE = ['#3b82f6', '#14b8a6', '#9333ea', '#f59e0b', '#ef4444', '#0ea5e9'];

function leaderboardColor(name) {
  if (!name) return LEADERBOARD_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % LEADERBOARD_PALETTE.length;
  return LEADERBOARD_PALETTE[hash];
}

function rankBadgeClass(idx) {
  if (idx === 0) return 'gold';
  if (idx === 1) return 'silver';
  if (idx === 2) return 'bronze';
  return '';
}

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'previous_week', label: 'Previous Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'month', label: 'By Month' },
  { value: 'custom', label: 'Custom Range' }
];

// Context builders forced strictly to Checklist Data
const buildChecklistContext = (title, extra = {}) => ({
  title,
  checklist: { ...extra, drillthrough: true },
  delegation: {} // Empty to prevent fetching
});

// Custom Date Range Picker Component (Same as before)
function CustomDateRangePicker({ startDate, endDate, onDateChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [step, setStep] = useState(startDate && !endDate ? 'end' : 'start');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const nextMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  const parseDateLocal = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-');
    return new Date(y, m - 1, d);
  };
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'MM/DD/YYYY';
    const [y, m, d] = dateStr.split('-');
    return `${m}/${d}/${y}`;
  };

  const handleDayClick = (date) => {
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (step === 'start') {
      onDateChange(dateString, '');
      setStep('end');
    } else {
      const start = parseDateLocal(startDate);
      if (date < start) {
        onDateChange(dateString, startDate);
      } else {
        onDateChange(startDate, dateString);
      }
      setStep('start');
      setIsOpen(false);
    }
  };

  const renderCalendar = (year, month, position) => {
    const days = [];
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

    return (
      <div className="calendar-pane">
        <div className="calendar-header">
          {position === 'left' ? <button type="button" onClick={handlePrevMonth}>&lt;</button> : <div></div>}
          <span>{new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
          {position === 'right' ? <button type="button" onClick={handleNextMonth}>&gt;</button> : <div></div>}
        </div>
        <div className="calendar-grid">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => <div key={idx} className="calendar-day-name">{d}</div>)}
          {days.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} className="calendar-cell empty"></div>;
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const isStart = startDate === dateStr;
            const isEnd = endDate === dateStr;
            let inRange = false;
            if (startDate && endDate) {
              inRange = date > parseDateLocal(startDate) && date < parseDateLocal(endDate);
            } else if (startDate && hoverDate && step === 'end') {
              const start = parseDateLocal(startDate);
              inRange = (date > start && date < hoverDate) || (date < start && date > hoverDate);
            }
            let classes = "calendar-cell";
            if (isStart) classes += " range-start";
            if (isEnd) classes += " range-end";
            if (inRange) classes += " in-range";
            return (
              <div key={i} className={classes} onClick={() => handleDayClick(date)} onMouseEnter={() => setHoverDate(date)}>
                {date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-wrapper" ref={wrapperRef}>
      <div className={`date-range-input ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <span>{formatDisplayDate(startDate)} &ndash; {formatDisplayDate(endDate)}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </div>
      {isOpen && (
        <div className="date-range-popup">
          {renderCalendar(viewDate.getFullYear(), viewDate.getMonth(), 'left')}
          <div className="calendar-divider"></div>
          {renderCalendar(nextMonth.getFullYear(), nextMonth.getMonth(), 'right')}
        </div>
      )}
    </div>
  );
}

// --- amCharts Sub-components ---
const TaskStatusChart = ({ data, openDrillMenu, buildChecklistContext }) => {
  const chartRef = useRef(null);

  useLayoutEffect(() => {
    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, { layout: root.horizontalLayout, innerRadius: am5.percent(60) })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, { valueField: 'value', categoryField: 'name' })
    );

    const statusToFilter = {
      'on time': { completion_filter: 'on_time' },
      'late': { completion_filter: 'late' },
      'overdue': { completion_filter: 'overdue' },
      'pending': { completion_filter: 'pending' },
      'in progress': { status_filter: 'in progress' }
    };

    series.slices.template.setAll({
      tooltipText: '{category}: {value} ({valuePercentTotal.formatNumber("0.0")}%)',
      strokeWidth: 2, stroke: am5.color(0xffffff)
    });

    // Extract the click handler into a reusable function
    const handleChartInteraction = (ev) => {
      const ctx = ev.target?.dataItem?.dataContext;
      const domEvent = ev.originalEvent || ev.event || window.event;
      if (!ctx || !domEvent) return;
      domEvent.preventDefault?.();
      domEvent.stopPropagation?.();
      
      // Note: Checklist uses statusToFilter instead of colorToFilter
      const filterObj = statusToFilter[String(ctx.name || '').trim().toLowerCase()];
      if (!filterObj) return;
      openDrillMenu(domEvent, buildChecklistContext(`${ctx.name} Checklist Tasks`, filterObj));
    };

    // Bind to BOTH contextmenu (right click) and click (left click)
    series.slices.template.events.on('click', handleChartInteraction);

    series.slices.template.adapters.add("fill", (fill, target) =>
      target.dataItem && target.dataItem.dataContext.color ? am5.color(target.dataItem.dataContext.color) : fill
    );

    series.labels.template.set("forceHidden", true);
    series.ticks.template.set("forceHidden", true);

    const chartData = data.map((item) => {
      const value = Number(item.value) || 0;
      const total = data.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
      return { ...item, value, percent: total > 0 ? ((value / total) * 100).toFixed(1) : "0.0" };
    });

    series.data.setAll(chartData);

    let legend = chart.children.push(
      am5.Legend.new(root, { centerY: am5.percent(50), y: am5.percent(50), layout: root.verticalLayout, width: am5.percent(55), paddingLeft: 12 })
    );

    legend.labels.template.setAll({ fontSize: 12, fill: am5.color(0x334155), oversizedBehavior: "wrap", maxWidth: 140 });
    legend.valueLabels.template.setAll({ fontSize: 14, fontWeight: "700", fill: am5.color(0xffffff) });
    legend.valueLabels.template.adapters.add("text", (text, target) => {
      const dataContext = target.dataItem?.dataContext;
      return dataContext ? `${Number(dataContext.value) || 0} (${dataContext.percent || "0.0"}%)` : text;
    });

    legend.markers.template.setAll({ width: 12, height: 12 });
    legend.markerRectangles.template.setAll({ cornerRadiusTL: 3, cornerRadiusTR: 3, cornerRadiusBL: 3, cornerRadiusBR: 3 });
    legend.valueLabels.template.set("text", "{value} ({valuePercentTotal.formatNumber('0.0')}%)");
    legend.data.setAll(series.dataItems);

    return () => root.dispose();
  }, [data, openDrillMenu]);

  return <div ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};

const WeeklyTrendChart = ({ data }) => {
  const chartRef = useRef(null);

  useLayoutEffect(() => {
    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false, panY: false, layout: root.verticalLayout,
        paddingTop: 8, paddingRight: 12, paddingBottom: 0, paddingLeft: 4
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "weekKey",
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 62, cellStartLocation: 0.12, cellEndLocation: 0.88 })
      })
    );

    xAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color(0x94a3b8), oversizedBehavior: "truncate", maxWidth: 88, textAlign: "center", dy: 8 });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) }));
    yAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color(0x94a3b8), paddingRight: 8 });

    const sorted = [...data].sort((a, b) => new Date(a.week) - new Date(b.week));
    const formattedData = sorted.map(d => {
      const date = new Date(d.week);
      const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
      const weekOfMonth = Math.ceil((date.getDate() + firstDayOfMonth) / 7);
      return { ...d, weekKey: d.week, weekLabel: `${date.toLocaleDateString(undefined, { month: 'short' })} Week ${weekOfMonth}` };
    });

    xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => target.dataItem ? target.dataItem.dataContext.weekLabel : text);
    xAxis.data.setAll(formattedData);

    const createSeries = (name, field, colorHex) => {
      const series = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name, xAxis, yAxis, valueYField: field, categoryXField: "weekKey",
          stroke: am5.color(colorHex), fill: am5.color(colorHex),
          tooltip: am5.Tooltip.new(root, { labelText: "{name}: {valueY}" })
        })
      );
      series.strokes.template.setAll({ strokeWidth: 2.4 });
      series.fills.template.setAll({ fillOpacity: 0.18, visible: true });
      series.data.setAll(formattedData);
      series.appear(900);
    };

    createSeries("Completed", "completed", 0x22c55e);
    createSeries("Overdue", "overdue", 0xef4444);

    chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, layout: root.horizontalLayout, marginTop: 10 }));
    legend.labels.template.setAll({ fontSize: 12, fill: am5.color(0x334155) });
    legend.data.setAll(chart.series.values);
    chart.appear(900, 80);
    return () => root.dispose();
  }, [data]);

  return <div ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};

const DepartmentChart = ({ data, openDrillMenu }) => {
  const chartRef = useRef(null);
  useLayoutEffect(() => {
    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false, layout: root.verticalLayout, paddingTop: 4, paddingRight: 10, paddingBottom: 4, paddingLeft: 6 }));
    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, { categoryField: "name", renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 14 }) }));
    yAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color(0x475569), oversizedBehavior: "wrap", maxWidth: 120, forceHidden: false });
    yAxis.data.setAll(data);

    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { min: 0, max: 100, strictMinMax: true, numberFormat: "#'%'", renderer: am5xy.AxisRendererX.new(root, {}) }));
    xAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color(0x94a3b8), paddingTop: 6 });
    xAxis.get("renderer").grid.template.setAll({ strokeOpacity: 0.12 });

    const series = chart.series.push(am5xy.ColumnSeries.new(root, { xAxis, yAxis, valueXField: "onTimeRate", categoryYField: "name", tooltip: am5.Tooltip.new(root, { labelText: "{categoryY}: {valueX}% on-time rate" }) }));
    series.columns.template.setAll({ cornerRadiusTR: 8, cornerRadiusBR: 8, height: am5.percent(58), strokeOpacity: 0 });
    series.columns.template.adapters.add("fill", () => am5.color(0x3b82f6));

    series.columns.template.events.on("click", (ev) => {
      const item = ev.target?.dataItem?.dataContext;
      if (!item) return;
      const domEvent = ev.originalEvent || ev.event || window.event;
      if (domEvent?.preventDefault) domEvent.preventDefault();
      domEvent?.stopPropagation?.();
      if (openDrillMenu) {
        const ctx = buildChecklistContext(`${item.name} - On Time Checklist Tasks`, { department_filter: item.id, completion_filter: 'on_time' });
        ctx.layout = 'department';
        openDrillMenu(domEvent, ctx);
      }
    });

    series.bullets.push(() => {
      const label = am5.Label.new(root, { text: "", centerX: am5.p100, centerY: am5.p50, dx: -8, fontSize: 12, fontWeight: "700", fill: am5.color(0xffffff) });
      label.adapters.add("text", (text, target) => {
        const v = target.dataItem?.get("valueX");
        return v != null && Number(v) > 0 ? `${Number(v).toFixed(0)}%` : "";
      });
      return am5.Bullet.new(root, { locationX: 1, sprite: label });
    });

    series.data.setAll(data);
    series.appear(900);
    chart.appear(900, 80);
    return () => root.dispose();
  }, [data, openDrillMenu]);
  return <div ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};

// --- Drillthrough Layers (Stripped of Delegation) ---
function DrillthroughLayers({ drillMenu, closeDrillMenu, drillPanel, closeDrillPanel, openChecklistDrillthrough, departments }) {
  if (!drillMenu && !drillPanel?.open) return null;

  const menuStyle = { position: 'fixed', top: drillMenu ? drillMenu.y : 0, left: drillMenu ? drillMenu.x : 0, zIndex: 99999, minWidth: 230, padding: 8, borderRadius: 14, border: '1px solid rgba(215, 225, 235, 0.95)', background: '#fff', boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)' };
  const itemStyle = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '10px 12px', border: 'none', borderRadius: 10, background: 'transparent', color: '#102033', fontSize: 12, fontWeight: 800, cursor: 'pointer', textAlign: 'left' };
  const overlayStyle = { position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(15, 23, 42, 0.18)', backdropFilter: 'blur(1px)' };
  const panelStyle = { position: 'absolute', top: 56, right: 24, width: 'min(760px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 88px)', display: 'flex', flexDirection: 'column', borderRadius: 20, border: '1px solid rgba(215, 225, 235, 0.95)', background: '#fff', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)', overflow: 'hidden' };

  const isDepartmentLayout = drillPanel.layout === 'department';
  const allTasks = [...(drillPanel.checklistRows || [])];
  const tasksByDept = isDepartmentLayout ? allTasks.reduce((acc, task) => {
    const dId = task.departmentId || 'unassigned';
    if (!acc[dId]) acc[dId] = [];
    acc[dId].push(task);
    return acc;
  }, {}) : {};

  const getDeptName = (id) => {
    if (id === 'unassigned') return 'Unassigned / Unknown Department';
    const dept = (departments || []).find(d => String(d.id) === String(id));
    return dept ? dept.dept_name : `Department ${id}`;
  };

  return (
    <>
      {drillMenu && (
        <div style={menuStyle} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onClick={(e) => e.preventDefault()}>
          <button type="button" style={itemStyle} onClick={() => {
            const ctx = drillMenu.context || {};
            closeDrillMenu();
            openChecklistDrillthrough(ctx.title || 'Drillthrough', ctx);
          }}>
            View Checklist Data
          </button>
        </div>
      )}

      {drillPanel?.open && (
        <div style={overlayStyle} onClick={closeDrillPanel}>
          <aside style={panelStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #edf2f7' }}>
              <h3 style={{ margin: 0 }}>{drillPanel.title}</h3>
            </div>
            <div style={{ flex: '1 1 auto', overflow: 'auto', padding: 16 }}>
              {drillPanel.loading ? (
                <div className="empty-state">Loading tasks...</div>
              ) : isDepartmentLayout ? (
                allTasks.length === 0 ? (
                  <div className="empty-state">No tasks found.</div>
                ) : (
                  <>
                    {Object.entries(tasksByDept).map(([deptId, tasks]) => {
                      if (tasks.length === 0) return null; 
                      return (
                        <div key={`dept-group-${deptId}`} style={{ marginBottom: '24px' }}>
                          <h4 style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', marginBottom: '12px', borderBottom: '2px solid #edf2f7', paddingBottom: '6px' }}>
                           <strong>{getDeptName(deptId)}</strong> <span style={{ color: '#94a3b8', fontWeight: 'normal', textTransform: 'none' }}><strong>({tasks.length} Tasks)</strong></span>
                          </h4>
                          {tasks.map((row, idx) => (
                            <div key={`${row.source}-${row.id}-${idx}`} style={{ border: '1px solid #edf2f7', borderRadius: 12, padding: 12, marginBottom: 10, background: '#f8fafc', position: 'relative' }}>
                              <div style={{ paddingRight: '80px' }}>
                                <strong>{row.title}</strong> 
                                <span style={{ fontSize: '13px', color: '#94a3b8', marginLeft: '6px' }}><strong>#{row.uid || row.taskId}</strong></span>
                              </div>
                              <span style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '10px', background: '#e0e7ff', color: '#4f46e5', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                {row.source}
                              </span>
                              <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#475569', marginTop: '8px' }}>
                                <span><strong>Status:</strong> <span style={{ color: row.status.toLowerCase() === 'completed' ? '#16a34a' : '#ea580c' }}>{row.status}</span></span>
                                <span><strong>Planned / Due:</strong> {row.due ? new Date(row.due).toLocaleDateString() : '-'}</span>
                                <span><strong>Delivered:</strong> {row.deliveredDate ? new Date(row.deliveredDate).toLocaleDateString() : '-'}</span>
                              </div>
                              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}><strong>Assignee:</strong> {row.assignee}</p>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </>
                )
              ) : (
                <>
                  {(!drillPanel.checklistRows || drillPanel.checklistRows.length === 0) && (
                    <div className="empty-state">No checklist tasks found.</div>
                  )}

                  {drillPanel.checklistRows?.length > 0 && (
                    <>
                      <h4 style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', marginBottom: '8px' }}>Checklist Tasks</h4>
                      {drillPanel.checklistRows.map(row => (
                        <div key={`ck-${row.id}`} style={{ border: '1px solid #edf2f7', borderRadius: 12, padding: 12, marginBottom: 10, background: '#f8fafc' }}>
                           {row.title} <span style={{ fontSize: '13px', color: '#94a3b8', marginLeft: '6px' }}><strong>#{row.uid}</strong></span>
                           <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#475569' }}>
                            <span><strong>Status:</strong> {row.status}</span>
                            <span><strong>Planned:</strong> {row.due || '-'}</span>
                            <span><strong>Delivered:</strong> {row.deliveredDate || '-'}</span>
                            {row.source === 'Checklist' && (
    <span><strong>Frequency:</strong> {row.frequency || '-'}</span>
  )}
                           </div>
                           <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}><strong>{row.assignee}</strong></p>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}


// --- Skeleton Loading Component ---
const DashboardSkeleton = () => (
  <div style={{ display: 'contents' }}>
    {/* KPI Skeletons */}
    <div className="quick-stats-row">
      {[...Array(5)].map((_, i) => (
        <div className="stat-card" key={i} style={{ borderBottom: '4px solid #e2e8f0' }}>
          <div className="skeleton skeleton-avatar" style={{ width: 46, height: 46, flex: '0 0 46px' }}></div>
          <div className="stat-info" style={{ width: '100%' }}>
            <div className="skeleton skeleton-text" style={{ width: '50%' }}></div>
            <div className="skeleton skeleton-text" style={{ width: '80%', height: '24px', margin: '6px 0' }}></div>
            <div className="skeleton skeleton-text" style={{ width: '40%' }}></div>
          </div>
        </div>
      ))}
    </div>

    {/* Chart Skeletons */}
    <div className="charts-grid">
      <div className="chart-box combined-card">
        <div className="chart-heading">
          <div className="skeleton skeleton-text" style={{ width: '30%', height: '20px', margin: 0 }}></div>
          <div className="skeleton skeleton-text" style={{ width: '80px', height: '20px', borderRadius: '999px', margin: 0 }}></div>
        </div>
        <div className="combined-card-grid">
          <div className="skeleton skeleton-block"></div>
          <div className="skeleton skeleton-block"></div>
        </div>
      </div>

      <div className="chart-box side-card">
        <div className="chart-heading">
          <div className="skeleton skeleton-text" style={{ width: '40%', height: '20px', margin: 0 }}></div>
        </div>
        <div className="simple-leaderboard">
          {[...Array(5)].map((_, i) => (
            <div className="leaderboard-item" key={i} style={{ background: 'transparent', borderColor: '#e2e8f0' }}>
              <div className="skeleton skeleton-avatar" style={{ width: 38, height: 38 }}></div>
              <div style={{ width: '100%' }}>
                <div className="skeleton skeleton-text" style={{ width: '60%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '40%', marginBottom: 0 }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="chart-box status-card">
        <div className="chart-heading">
          <div className="skeleton skeleton-text" style={{ width: '40%', height: '20px', margin: 0 }}></div>
        </div>
        <div className="skeleton skeleton-block" style={{ borderRadius: '50%', maxWidth: '200px', maxHeight: '200px', margin: 'auto' }}></div>
      </div>

      <div className="chart-box department-card">
        <div className="chart-heading">
          <div className="skeleton skeleton-text" style={{ width: '40%', height: '20px', margin: 0 }}></div>
        </div>
        <div className="skeleton skeleton-block"></div>
      </div>
    </div>
  </div>
);

const ChecklistDashboard = ({
  user,
  onLogout,
  loggingOut = false,
  onNavigateToDashboard, // Added switch to standard dashboard
  onNavigateToChecklistDashboard, // Active state
  onNavigateToDelegationDashboard, // Added linking hook
  onNavigateToFMS,
  onNavigateToReturns,
  onNavigateToDelegation,
  onNavigateToAllTasks,
  onNavigateToResourceControl,
  onNavigateToLogging,
  onNavigateToTasks
}) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [dateFilter, setDateFilter] = useState('this_week');
  const [monthFilter, setMonthFilter] = useState(String(new Date().getMonth() + 1));
  const [customStart, setCustomStart] = useState(getTodayString());
  const [customEnd, setCustomEnd] = useState(getTodayString());
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');

  // Dropdown Options
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const hasDashboardAccess = () => user && user.resource_access && user.resource_access.Dashboard === true;
  
  const [drillMenu, setDrillMenu] = useState(null);
  const [drillPanel, setDrillPanel] = useState({ open: false, loading: false, title: '', rows: [], source: '' });
  const accessGranted = hasDashboardAccess();
  useEffect(() => {
    document.title = 'Checklist Dashboard';
  }, []);
  useEffect(() => {
    const fetchFilterData = async () => {
      if (user?.role !== 1 && user?.role !== 2) return;
      try {
        const [deptRes, empRes] = await Promise.all([
          fetch('/api/tasks/departments', { headers: { 'Authorization': `Bearer ${user?.authToken}` } }),
          fetch('/api/tasks/users', { headers: { 'Authorization': `Bearer ${user?.authToken}` } })
        ]);
        const deptData = await deptRes.json();
        const empData = await empRes.json();
        if (Array.isArray(deptData)) setDepartments(deptData);
        if (Array.isArray(empData)) setEmployees(empData);
      } catch (err) {
        console.error("Failed to load filter dropdowns", err);
      }
    };
    if (accessGranted) fetchFilterData();
  }, [user, accessGranted]);

  useEffect(() => {
    if (!accessGranted) return;
    if (dateFilter === 'custom' && (!customStart || !customEnd)) return;

    const fetchMetrics = async () => {
      setLoading(true);
      try {
        // Appending &source=checklist to the endpoint
        let url = `/api/dashboard/metrics?date_filter=${dateFilter}&source=checklist`;

        if (dateFilter === 'custom') { url += `&start_date=${customStart}&end_date=${customEnd}`; }
        if (dateFilter === 'month') { url += `&month=${selectedMonth}`; }
        if (employeeFilter) url += `&employee_filter=${employeeFilter}`;
        if (departmentFilter) url += `&department_filter=${departmentFilter}`;

        const response = await fetch(url, { headers: { 'Authorization': `Bearer ${user?.authToken}` } });
        const data = await response.json();
        if (data.success) {
          setMetrics(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch metrics", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [user, accessGranted, dateFilter, customStart, customEnd, employeeFilter, departmentFilter, selectedMonth]);

  const statusBreakdown = (metrics?.statusBreakdown || []).filter(s => s.value > 0);
  const departmentPerformance = metrics?.departmentPerformance || [];
  const leaderboard = metrics?.leaderboard || [];
  const weeklyTrend = metrics?.weeklyTrend || [];

  const buildCurrentQuery = (extra = {}) => {
    const params = new URLSearchParams();
    params.set('date_filter', dateFilter);
    if (dateFilter === 'custom') { params.set('start_date', customStart); params.set('end_date', customEnd); }
    if (dateFilter === 'month') { params.set('month', String(selectedMonth)); }
    if (departmentFilter) params.set('department_filter', departmentFilter);
    if (employeeFilter) params.set('employee_filter', employeeFilter);
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(key, String(value));
      }
    });
    return params.toString();
  };

  // Left-click now opens the drillthrough panel directly (no intermediate menu).
  const openDrillMenu = (event, context) => {
    event.preventDefault?.();
    event.stopPropagation?.();
    openChecklistDrillthrough(context?.title || 'Drillthrough', context || {});
  };

  const closeDrillMenu = () => setDrillMenu(null);
  const closeDrillPanel = () => setDrillPanel({ open: false, loading: false, title: '', rows: [], source: '' });

  const flattenChecklistResponse = (payload) => {
  const columns = Array.isArray(payload?.columns) ? payload.columns : [];

  return columns.flatMap((col) =>
    (col.cards || []).map((task) => ({
      source: 'Checklist',
      id: task.id,
      uid: task.uid,
      title: task.title,
      status: task.status,
      assignee: task.assigneeName || task.assignedToName || task.assigned_to_name || '-',
      departmentId: task.departmentId,
      due: task.due,
      deliveredDate: task.deliveredDate,
      frequency: task.frequency || task.task_frequency || task.Frequency || '-',
      description: task.description,
      subtitle: task.subtitle,
      raw: task
    }))
  );
};

  const openChecklistDrillthrough = async (title, context) => {
    setDrillPanel({ open: true, loading: true, title, checklistRows: [], layout: context.layout });
    try {
      const hasChecklist = context.checklist && Object.keys(context.checklist).length > 0;
      const checklistPromise = hasChecklist 
        ? fetch(`/api/tasks/task_lists/drillthrough?${buildCurrentQuery(context.checklist)}`, {
            credentials: 'include',
            headers: { Authorization: `Bearer ${user?.authToken}` }
          }).then(r => r.json().then(data => ({ type: 'checklist', data, ok: r.ok })))
        : Promise.resolve({ type: 'checklist', data: null, ok: false });

      const results = await Promise.all([checklistPromise]);
      const stateUpdate = { loading: false, checklistRows: [] };
      results.forEach(res => {
        if (res.type === 'checklist' && res.ok) stateUpdate.checklistRows = flattenChecklistResponse(res.data);
      });
      setDrillPanel((prev) => ({ ...prev, ...stateUpdate }));
    } catch (error) {
      console.error("Drillthrough error:", error);
      setDrillPanel((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') { closeDrillMenu(); closeDrillPanel(); } };
    const handleMouseDown = () => closeDrillMenu();
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('mousedown', handleMouseDown); };
  }, []);

  return (
    <div className="app-shell dashboard-shell">
      <AppSidebar
        user={user}
        active="dashboard"
        onLogout={onLogout}
        loggingOut={loggingOut}
        onNavigateToDashboard={onNavigateToDashboard}
        onNavigateToTasks={onNavigateToTasks}
        onNavigateToFMS={onNavigateToFMS}
        onNavigateToReturns={onNavigateToReturns}
        onNavigateToDelegation={onNavigateToDelegation}
        onNavigateToAllTasks={onNavigateToAllTasks}
        onNavigateToResourceControl={onNavigateToResourceControl}
        onNavigateToLogging={onNavigateToLogging}
      />

      <main className="workspace">
        <div className="simple-dashboard">
          {!accessGranted ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px', textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '32px 40px', maxWidth: '500px' }}>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '700', color: '#92400e' }}>🔒 Access Denied</h2>
                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#b45309', lineHeight: '1.6' }}>You don't have access to the Dashboard. Please contact your administrator to request access.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="dash-header">
                <div>
                  <h1>Checklist Performance Dashboard</h1>
                  <p>Real-time checklist metrics and performance tracking</p>
                </div>
                <div className="dash-filters" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select 
  className="simple-select" 
  style={{ backgroundColor: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0', cursor: 'pointer' }}
  value="checklist" 
  onChange={(e) => {
    if (e.target.value === 'main') onNavigateToDashboard();
    if (e.target.value === 'delegation') onNavigateToDelegationDashboard(); // Added linking hook
  }}
>
  <option value="main">All Data (Combined)</option>
  <option value="checklist">Checklist Tasks Only</option>
  <option value="delegation">Delegation Records Only</option>
</select>
                  <select className="simple-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                    {DATE_FILTER_OPTIONS.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                  </select>
                  {dateFilter === 'month' && (
                    <select className="simple-select" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{new Date(0, m-1).toLocaleString('default', { month: 'long' })}</option>)}
                    </select>
                  )}
                  {dateFilter === 'custom' && (
                    <CustomDateRangePicker startDate={customStart} endDate={customEnd} onDateChange={(start, end) => { setCustomStart(start); setCustomEnd(end); }} />
                  )}
                  {(user?.role == 1 || user?.role == 2 || String(user?.role).toLowerCase() === 'admin' || String(user?.role).toLowerCase() === 'mis') && (
                    <>
                      <select className="simple-select" value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
                        <option value="">All Employees</option>
                        {employees.map(emp => (<option key={emp.id} value={emp.id}>{emp.emp_name}</option>))}
                      </select>
                      <select className="simple-select" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                        <option value="">All Departments</option>
                        {departments.map(dept => (<option key={dept.id} value={dept.id}>{dept.dept_name}</option>))}
                      </select>
                    </>
                  )}
                </div>
              </div>

              {loading ? (
                <DashboardSkeleton />
              ) : (
                <div className="fade-in-content">
                  <div className="quick-stats-row" >
                    <div className="stat-card" style={{ borderBottom: '4px solid #9333ea' }} onClick={(e) => openDrillMenu(e, buildChecklistContext('Total Tasks', {}))}>
                      <div className="stat-icon" style={{ background: '#f3e8ff', color: '#9333ea' }}>
                        <svg width="20" height="20" viewBox="0 0 47 47" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.12,38.52V5.136h26.962v28.037l5.137-4.243V2.568C38.219,1.15,37.07,0,35.652,0h-32.1C2.134,0,0.985,1.15,0.985,2.568 v38.519c0,1.418,1.149,2.568,2.567,2.568h22.408L22.33,38.52H6.12z"></path><path d="M45.613,27.609c-0.473-0.446-1.2-0.467-1.698-0.057l-11.778,9.734l-7.849-4.709c-0.521-0.312-1.188-0.219-1.603,0.229 c-0.412,0.444-0.457,1.117-0.106,1.613l8.506,12.037c0.238,0.337,0.625,0.539,1.037,0.543c0.004,0,0.008,0,0.012,0 c0.408,0,0.793-0.193,1.035-0.525l12.6-17.173C46.149,28.78,46.084,28.055,45.613,27.609z"></path><path d="M27.306,8.988H11.897c-1.418,0-2.567,1.15-2.567,2.568s1.149,2.568,2.567,2.568h15.408c1.418,0,2.566-1.15,2.566-2.568 S28.724,8.988,27.306,8.988z"></path><path d="M27.306,16.691H11.897c-1.418,0-2.567,1.15-2.567,2.568s1.149,2.568,2.567,2.568h15.408c1.418,0,2.566-1.149,2.566-2.568 C29.874,17.841,28.724,16.691,27.306,16.691z"></path><path d="M27.306,24.395H11.897c-1.418,0-2.567,1.15-2.567,2.568s1.149,2.568,2.567,2.568h15.408c1.418,0,2.566-1.15,2.566-2.568 C29.874,25.545,28.724,24.395,27.306,24.395z"></path></svg>
                      </div>
                      <div className="stat-info">
                        <span className="stat-label">Total Checklist Tasks</span>
                        <span className="stat-value" style={{ color: '#9333ea' }}>{metrics?.totalTasks}</span>
                        <span className="stat-detail">All recorded tasks</span>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderBottom: '4px solid #2563eb' }} onClick={(e) => openDrillMenu(e, buildChecklistContext('Total Active Tasks', { completion_filter: 'active' }))}>
                      <div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
                        <svg width="32" height="38" viewBox="0 0 207.348 207.347" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M35.974,56.681c8.659,0,15.683-10.044,15.683-18.706c0-8.665-7.024-15.686-15.683-15.686 c-8.668,0-15.695,7.021-15.695,15.686C20.279,46.637,27.306,56.681,35.974,56.681z"></path><path d="M66.682,67.574c-0.119-0.429-0.204-0.874-0.411-1.272c-0.186-0.359-0.448-0.649-0.691-0.953s-0.462-0.636-0.761-0.898 c-0.356-0.305-0.761-0.5-1.172-0.719c-0.262-0.128-0.457-0.341-0.743-0.441c-0.56-0.204-13.049-4.683-24.524-5.267l-1.577,3.148 h-0.037l4.171,32.534l-4.963,8.681l-4.957-8.681l4.171-32.534h-0.045L33.59,58.06c-10.461,0.53-21.735,4.253-24.085,5.063 c-0.107,0.037-0.198,0.073-0.299,0.107c-0.058,0.024-0.137,0.048-0.155,0.061c-0.125,0.046-0.207,0.143-0.326,0.195 c-0.6,0.262-1.166,0.566-1.647,0.996c-0.222,0.207-0.372,0.451-0.566,0.682c-0.304,0.372-0.624,0.715-0.84,1.151 c-0.195,0.387-0.277,0.807-0.386,1.23c-0.088,0.298-0.25,0.56-0.283,0.892l-4.963,44.667c-0.38,3.397,2.076,6.461,5.474,6.844c0.238,0.037,0.469,0.043,0.694,0.043c3.118,0,5.803-2.344,6.159-5.523l4.521-40.675c0.53-0.155,1.108-0.338,1.72-0.506v42.345 l-4.938,61.396c-0.329,4.092,2.725,7.685,6.823,8.008c0.198,0.018,0.405,0.023,0.603,0.023c3.842,0,7.097-2.947,7.408-6.844 l4.466-55.49c0.917,0.414,1.933,0.651,2.999,0.651c1.072,0,2.083-0.237,3.005-0.651l4.463,55.49 c0.313,3.896,3.574,6.844,7.407,6.844c0.201,0,0.405-0.006,0.606-0.023c4.104-0.323,7.152-3.916,6.82-8.008l-4.938-61.396V73.286 c0.618,0.168,1.199,0.351,1.729,0.506l4.521,40.675c0.347,3.18,3.035,5.523,6.159,5.523c0.225,0,0.454-0.019,0.691-0.043 c3.407-0.383,5.855-3.446,5.474-6.844l-4.962-44.667C66.919,68.123,66.758,67.867,66.682,67.574z"></path><path d="M161.332,82.59H87.385c-2.944,0-5.307,2.375-5.307,5.318c0,2.936,2.363,5.301,5.307,5.301h73.947 c2.947,0,5.31-2.365,5.31-5.301C166.636,84.965,164.267,82.59,161.332,82.59z"></path><path d="M161.332,106.076H87.385c-2.944,0-5.307,2.372-5.307,5.311c0,2.947,2.363,5.297,5.307,5.297h73.947 c2.947,0,5.31-2.35,5.31-5.297C166.642,108.448,164.267,106.076,161.332,106.076z"></path><path d="M161.332,129.543H87.385c-2.944,0-5.307,2.388-5.307,5.315c0,2.941,2.363,5.305,5.307,5.305h73.947 c2.947,0,5.31-2.363,5.31-5.305C166.636,131.931,164.267,129.543,161.332,129.543z"></path><polygon points="207.348,69.158 202.153,65.699 188.653,85.994 178.26,78.182 174.521,83.163 190.188,94.948"></polygon><path d="M189.427,104.593c3.751,0,6.783,3.039,6.783,6.78c0,3.751-3.032,6.79-6.783,6.79c-3.744,0-6.783-3.039-6.783-6.79 C182.644,107.632,185.683,104.593,189.427,104.593z"></path><path d="M189.427,128.064c3.751,0,6.783,3.039,6.783,6.783c0,3.745-3.032,6.783-6.783,6.783c-3.744,0-6.783-3.038-6.783-6.783 C182.644,131.103,185.683,128.064,189.427,128.064z"></path></svg>
                      </div>
                      <div className="stat-info" >
                        <span className="stat-label">Total Active Tasks</span>
                        <span className="stat-value" style={{ color: '#2563eb' }}>{metrics?.totalActive}</span>
                        <span className="stat-detail">Assigned to you or your team</span>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderBottom: '4px solid #22c55e' }} onClick={(e) => openDrillMenu(e, buildChecklistContext('On-Time Completion Rate', { completion_filter: 'on_time' }))}>
                      <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a', fontSize: '28px' }}>✓</div>
                      <div className="stat-info">
                        <span className="stat-label">On-Time Completion Rate</span>
                        <span className="stat-value" style={{ color: '#16a34a' }}>{metrics?.onTimeRate}%</span>
                        <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}><strong>{metrics?.onTimeCount}</strong> tasks delivered on time</span>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderBottom: '4px solid #f59e0b' }} onClick={(e) => openDrillMenu(e, buildChecklistContext('Not Done on Time', { completion_filter: 'late' }))}>
                      <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706', fontSize: '22px' }}>⏱</div>
                      <div className="stat-info">
                        <span className="stat-label">Not Done on Time</span>
                        <span className="stat-value" style={{ color: '#d97706' }}>{metrics?.lateCompletedRate}%</span>
                        <span className="stat-detail"><strong>{metrics?.lateCompletedCount}</strong> tasks delivered late</span>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderBottom: '4px solid #ef4444' }} onClick={(e) => openDrillMenu(e, buildChecklistContext('Pending / Overdue Tasks', { completion_filter: 'overdue' }))}>
                      <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626', fontSize: '22px' }}>✕</div>
                      <div className="stat-info">
                        <span className="stat-label">Work Not Done</span>
                        <span className="stat-value" style={{ color: '#dc2626' }}>{metrics?.incompleteRate}%</span>
                        <span className="stat-detail"><strong>{metrics?.incompleteCount}</strong> tasks overdue</span>
                      </div>
                    </div>
                  </div>

                  <div className="charts-grid">
                    <div className="chart-box combined-card" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <div className="chart-heading">
                        <h3 className="chart-title">Monthly Checklist Insights</h3>
                        <span className="chart-chip">Avg delay {metrics?.avgDelayDays ?? 0}d</span>
                      </div>
                      
                      {/* Notice: Revision Insights removed entirely to let the Trend Chart span naturally */}
                      <div className="combined-card-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                        <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid rgba(215, 225, 235, 0.65)', borderRadius: 18, background: '#f8fbff', padding: 14 }}>
                          <div className="chart-heading" style={{ marginBottom: 8 }}>
                            <h3 className="chart-title">Checklist Completion Trend</h3>
                          </div>
                          <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                            {weeklyTrend.length === 0 ? <div className="empty-state">Not enough history yet</div> : <WeeklyTrendChart data={weeklyTrend} />}
                          </div>
                          <p className="chart-note" style={{ marginTop: 8 }}>
                            Average delay on late tasks: <strong>{metrics?.avgDelayDays}</strong> days
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="chart-box side-card">
                      <div className="chart-heading">
                        <h3 className="chart-title">Top Performers</h3>
                        <span className="chart-chip">On-time rate</span>
                      </div>
                      {leaderboard.length === 0 ? (
                        <div className="empty-state">No completed tasks yet</div>
                      ) : (
                        <div className="simple-leaderboard">
                          {leaderboard.slice(0).map((person, idx) => (
                            <div
                              className="leaderboard-item"
                              key={idx}
                              style={{ cursor: 'pointer' }}
                              title="Click to view this user's late & overdue tasks"
                              onClick={(e) => openDrillMenu(e, buildChecklistContext(`${person.name} — Late & Overdue Tasks`, { employee_filter: person.id, completion_filter: 'late_overdue' }))}
                            >
                              <div className="lb-avatar-wrap">
                                <div className="lb-avatar" style={{ background: leaderboardColor(person.name) }}>{leaderboardInitials(person.name)}</div>
                              </div>
                              <div className="lb-details">
                                <strong>{person.name}</strong><span>{person.designation}</span>
                              </div>
                              <div className="lb-score" style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                                <div style={{ fontSize: '16px', fontWeight: '800' }}>
                                  <span
                                    style={{ color: '#d97706', cursor: 'pointer' }}
                                    title="View late tasks"
                                    onClick={(e) => { e.stopPropagation(); openDrillMenu(e, buildChecklistContext(`${person.name} — Late Tasks`, { employee_filter: person.id, completion_filter: 'late' })); }}
                                  >- {person.lateRate}% Late</span>
                                </div>
                                <span
                                  style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600', cursor: 'pointer' }}
                                  title="View overdue tasks"
                                  onClick={(e) => { e.stopPropagation(); openDrillMenu(e, buildChecklistContext(`${person.name} — Overdue Tasks`, { employee_filter: person.id, completion_filter: 'overdue' })); }}
                                >- {person.overdueRate}% work not done </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="chart-box status-card">
                      <div className="chart-heading">
                        <h3 className="chart-title">Checklist Status Breakdown</h3>
                        <span className="chart-chip">{metrics?.totalTasks ?? 0} total</span>
                      </div>
                      <div className="chart-body">
                        {statusBreakdown.length === 0 ? <div className="empty-state">No task data yet</div> : <TaskStatusChart data={statusBreakdown} buildChecklistContext={buildChecklistContext} openDrillMenu={openDrillMenu} />}
                      </div>
                      <p className="chart-note">
                        Out of <strong>{metrics?.totalTasks}</strong> total tasks · {metrics?.totalCompleted} completed
                      </p>
                    </div>

                    {departmentPerformance.length > 0 && (
                      <div className="chart-box department-card" onClick={(e) => {
                          e.preventDefault();
                          const ctx = buildChecklistContext('All Departments - On Time Tasks', { completion_filter: 'on_time' });
                          ctx.layout = 'department';
                          openDrillMenu(e, ctx);
                        }}
                      >
                        <div className="chart-heading">
                          <h3 className="chart-title">On-Time Rate by Dept.</h3>
                          <span className="chart-chip">{departmentPerformance.length} depts</span>
                        </div>
                        <div className="chart-body">
                         <DepartmentChart data={departmentPerformance} openDrillMenu={openDrillMenu} buildChecklistContext={buildChecklistContext} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <DrillthroughLayers drillMenu={drillMenu} closeDrillMenu={closeDrillMenu} drillPanel={drillPanel} closeDrillPanel={closeDrillPanel} openChecklistDrillthrough={openChecklistDrillthrough} departments={departments} />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChecklistDashboard;
